import React, { useState, useEffect } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { DotLottiePlayer } from '@dotlottie/react-player';
import { useNavigate } from 'react-router-dom';

const ClinicFlow = () => {
    const navigate = useNavigate();
    const { scrollY } = useScroll();
    const [activeFeature, setActiveFeature] = useState(0);

    useEffect(() => {
        const interval = setInterval(() => {
            setActiveFeature((prev) => (prev + 1) % 3);
        }, 2000);
        return () => clearInterval(interval);
    }, []);

    // Parallax effects
    const y1 = useTransform(scrollY, [0, 500], [0, 100]);
    const y2 = useTransform(scrollY, [0, 500], [0, -50]);

    return (
        <motion.div
            className="min-h-screen bg-[#F5F5F0] text-[#1A1A1A] font-sans selection:bg-[#2D5BFF] selection:text-white"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.8 }}
        >
            {/* Navigation - Top Left */}
            {/* Top-left HOME button - Vertical text */}
            <motion.button
                onClick={() => navigate('/')}
                className="fixed top-0 left-0 z-50 flex flex-col items-center group hidden md:flex"
                style={{
                    padding: '20px'
                }}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
            >
                <motion.span
                    className="text-[#1A1A1A] text-sm font-medium"
                    style={{
                        writingMode: 'vertical-rl',
                        textOrientation: 'mixed',
                        transform: 'rotate(180deg)'
                    }}
                    whileHover={{ letterSpacing: "0.2em" }}
                    transition={{ duration: 0.3, ease: "easeOut" }}
                >
                    HOME
                </motion.span>
                {/* Vertical line below HOME text */}
                <motion.div
                    className="w-[1px] bg-[#1A1A1A]"
                    initial={{ height: '80px', opacity: 0.6 }}
                    whileHover={{ height: '100px', opacity: 1 }}
                    transition={{ duration: 0.3, ease: "easeOut" }}
                    style={{
                        marginTop: '20px'
                    }}
                />
            </motion.button>

            {/* 2025 Vertical Text */}
            <div className="fixed left-8 bottom-8 z-50 hidden md:block">
                <span
                    className="text-[#1A1A1A]/40 text-[10px] font-sans tracking-widest block"
                    style={{ writingMode: 'vertical-rl', textOrientation: 'mixed' }}
                >
                    © 2025
                </span>
            </div>

            {/* Hero Section */}
            <section className="min-h-screen flex items-start px-6 md:pl-32 relative overflow-hidden pt-4 md:pt-20">
                <div className="flex flex-col md:flex-row w-full max-w-7xl items-center justify-between gap-12 md:gap-20">
                    {/* Mobile Top Bar - HOME + Line */}
                    <div className="md:hidden flex items-center mb-12 w-full gap-4">
                        <button
                            onClick={() => navigate('/')}
                            className="text-[#1A1A1A] text-sm font-medium tracking-widest uppercase"
                        >
                            HOME
                        </button>
                        <div className="h-[1px] bg-[#1A1A1A] w-24 opacity-20"></div>
                    </div>
                    <div className="z-10 w-full max-w-2xl text-left">
                        <motion.h1
                            className="text-[18vw] sm:text-[14vw] md:text-[8rem] leading-[0.8] font-serif mb-6 md:mb-8"
                            initial={{ y: 100, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
                        >
                            CLINIC<br />FLOW
                        </motion.h1>
                        <motion.div
                            className="flex flex-col gap-6 md:gap-8 items-start"
                            initial={{ y: 50, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            transition={{ delay: 0.3, duration: 1, ease: [0.22, 1, 0.36, 1] }}
                        >
                            <p className="max-w-md text-base md:text-lg leading-relaxed opacity-80">
                                A comprehensive Queue Management System designed to streamline patient flow and optimize hospital resources.
                            </p>
                            <div className="text-[10px] md:text-sm font-bold tracking-widest opacity-40 uppercase font-mono">
                                // Queue System<br />
                                // Reservation<br />
                                // Monitoring
                            </div>
                        </motion.div>
                    </div>

                    <motion.div
                        className="w-full md:w-1/2 max-w-[320px] sm:max-w-md md:max-w-lg"
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 1, delay: 0.2 }}
                    >
                        {/* Medical Shield Animation - Large right panel */}
                        <DotLottiePlayer
                            src="/assets/Medical Shield.lottie"
                            loop
                            autoplay
                            className="w-full h-auto"
                        />
                    </motion.div>
                </div>

                {/* Scroll Indicator */}
                <motion.div
                    className="absolute bottom-12 left-1/2 -translate-x-1/2 hidden md:block"
                    animate={{ y: [0, 10, 0] }}
                    transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                >
                    <div className="w-[1px] h-16 bg-black/20" />
                </motion.div>
            </section>

            {/* The Problem / Solution (Platform Loading Animation) */}
            <section className="min-h-screen flex md:items-center px-6 py-10 md:py-24 md:pl-32 bg-[#EAEAE5]">
                <div className="max-w-7xl w-full grid grid-cols-1 md:grid-cols-2 gap-16 md:gap-20 items-center">
                    <motion.div className="order-2 md:order-1" style={{ y: typeof window !== 'undefined' && window.innerWidth > 768 ? y1 : 0 }}>
                        {/* Platform Loading Queue Animation */}
                        <div className="bg-transparent -mt-10 md:-mt-20">
                            <DotLottiePlayer
                                src="/assets/Queue Users Search.lottie"
                                loop
                                autoplay
                                className="w-full h-auto max-w-sm mx-auto md:max-w-none"
                            />
                        </div>
                    </motion.div>

                    <div className="space-y-10 md:space-y-12 order-1 md:order-2">
                        <div className="space-y-4 md:space-y-6">
                            <h2 className="text-4xl md:text-6xl font-serif">Effortless<br />Reservations</h2>
                            <p className="text-base md:text-lg opacity-80 max-w-sm leading-relaxed">
                                Patients can easily reserve their schedule through a simple interface, choosing their preferred time and available rooms with just a few clicks.
                            </p>
                        </div>
                        <div>
                            <ul className="space-y-6 pl-8 relative">
                                {/* Vertical line track */}
                                <div className="absolute left-0 top-0 bottom-0 w-[1px] bg-black/10" />

                                {["Real-time Queue Updates", "Multiple Room Support", "Whatsapp Reminders"].map((feature, index) => (
                                    <motion.li
                                        key={index}
                                        className="text-lg md:text-xl font-medium relative"
                                        animate={{
                                            opacity: activeFeature === index ? 1 : 0.3,
                                            scale: activeFeature === index ? 1.05 : 1,
                                            color: activeFeature === index ? "#1A1A1A" : "#1A1A1A"
                                        }}
                                        transition={{ duration: 0.5 }}
                                    >
                                        {/* Active Indicator Line */}
                                        {activeFeature === index && (
                                            <motion.div
                                                layoutId="activeFeatureIndicator"
                                                className="absolute -left-[calc(2rem+1px)] top-1/2 -translate-y-1/2 w-[2px] h-6 md:h-8 bg-[#2D5BFF]"
                                                initial={{ height: 0 }}
                                                animate={{ height: typeof window !== 'undefined' && window.innerWidth > 768 ? 32 : 24 }}
                                                transition={{ duration: 0.3 }}
                                            />
                                        )}
                                        {feature}
                                    </motion.li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>
            </section>

            {/* Roles & Management (Doctors Animation) */}
            <section className="min-h-screen relative px-6 py-10 md:py-24 md:pl-32 bg-[#1A1A1A] text-[#F5F5F0] overflow-hidden">
                <div className="max-w-7xl w-full relative z-10">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-16 items-center">
                        <div className="order-2 md:order-1 space-y-8 md:space-y-12">
                            <h2 className="text-4xl md:text-6xl font-serif">Two-Role<br />Ecosystem</h2>

                            <div className="space-y-6 md:space-y-8 md:mt-12">
                                <div className="group border-t border-white/10 pt-6 md:pt-8 transition-colors hover:border-white/40">
                                    <h3 className="text-xl md:text-2xl font-bold mb-2">Superadmin</h3>
                                    <p className="text-sm md:text-base opacity-50 group-hover:opacity-100 transition-opacity max-w-sm md:max-w-md">
                                        Complete control over the facility. Manage staff accounts, edit appointments from the backend, and oversee the entire daily operation.
                                    </p>
                                </div>
                                <div className="group border-t border-white/10 pt-6 md:pt-8 transition-colors hover:border-white/40">
                                    <h3 className="text-xl md:text-2xl font-bold mb-2">Doctor & Staff</h3>
                                    <p className="text-sm md:text-base opacity-50 group-hover:opacity-100 transition-opacity max-w-sm md:max-w-md">
                                        Dedicated interface for medical professionals to view their upcoming schedule, manage queue flow, and access patient history.
                                    </p>
                                </div>
                            </div>
                        </div>

                        <motion.div
                            className="order-1 md:order-2 flex justify-center w-full"
                            style={{ y: typeof window !== 'undefined' && window.innerWidth > 768 ? y2 : 0 }}
                        >
                            {/* Doctors Animation */}
                            <div className="relative w-full max-w-md">
                                <div className="absolute inset-0 bg-[#2D5BFF] rounded-full blur-[80px] md:blur-[100px] opacity-10"></div>
                                <DotLottiePlayer
                                    src="/assets/doctors.lottie"
                                    loop
                                    autoplay
                                    className="w-full h-auto relative z-10"
                                />
                            </div>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="py-24 text-center">
                <h2 className="text-[10vw] font-serif leading-none opacity-10 select-none">CLINICFLOW</h2>
            </footer>

        </motion.div>
    );
};

export default ClinicFlow;
